import { Form, Link, useActionData, useNavigation } from "@remix-run/react";
import { useEffect, useRef, useState } from "react";
import {
  TextInput, Button, Group, Stack, Title, Text, Anchor, Alert, rem
} from '@mantine/core';
import { useForm, isEmail } from '@mantine/form';
import { IconAt, IconAlertCircle, IconBuilding, IconArrowLeft, IconMail } from '@tabler/icons-react';

import type { action as forgotPasswordAction } from "./action.server";
import loginBg01 from "~/assets/images/login-bg-01.jpg";

export { loader } from "./loader.server";
export { action } from "./action.server";

export const meta = () => [
  { title: "Hub Desk - Şifremi Unuttum" },
  { name: "description", content: "Hub Desk uygulaması şifre sıfırlama sayfası" },
];

export const handle = {
  backgroundImage: loginBg01,
};

export default function ForgotPassword() {
  const actionData = useActionData<typeof forgotPasswordAction>();
  const navigation = useNavigation();
  const isSubmitting = navigation.formAction === "/forgot-password" && navigation.state === "submitting";
  const [isAlertVisible, setIsAlertVisible] = useState(true);

  const emailRef = useRef<HTMLInputElement>(null);

  const form = useForm({
    initialValues: {
      email: actionData?.fields?.email ?? '',
      tenant: actionData?.fields?.tenant ?? (process.env.NODE_ENV === 'development' ? 'root' : ''),
    },
    validate: {
      email: isEmail('Geçerli bir e-posta adresi giriniz'),
      tenant: (value) => !value ? 'Tenant seçimi zorunludur' : null,
    },
  });

  useEffect(() => {
    if (actionData?.errors?.email) emailRef.current?.focus();

    // Form hatası varsa alert'i göster
    if (actionData?.errors?.form) {
      setIsAlertVisible(true);
    }
  }, [actionData]);

  return (
    <div>
      {actionData?.errors?.form && isAlertVisible && (
        <Alert
          icon={<IconAlertCircle size={24} />}
          title="Hata"
          variant="filled"
          color="red"
          mb="md"
          withCloseButton
          onClose={() => setIsAlertVisible(false)}
        >
          {actionData.errors.form}
        </Alert>
      )}

      {actionData?.success && (
        <Alert
          icon={<IconMail size={24} />}
          title="Başarılı"
          variant="filled"
          color="green"
          mb="md"
        >
          Şifre sıfırlama bağlantısı e-posta adresinize gönderildi. Lütfen e-posta kutunuzu kontrol edin.
        </Alert>
      )}

      <Form method="post" replace>
        <Stack gap="lg">
          <Group justify="space-between">
            <Title order={2} c={"white"}>Şifremi Unuttum</Title>
            <IconMail color="white" size={48} />
          </Group>

         

          <Stack gap="xs">
            <Text size="xs" c={"white"}>Tenant</Text>
            <TextInput
              placeholder="Tenant adını giriniz"
              leftSection={<IconBuilding style={{ width: rem(18), height: rem(18) }} />}
              name="tenant"
              error={actionData?.errors?.tenant || form.errors.tenant}
              disabled={isSubmitting}
              {...form.getInputProps('tenant')}
              data-testid="tenant-input"
              size="md"
            />
          </Stack>

          <Stack gap="xs">
            <Text size="xs" c={"white"}>E-posta</Text>
            <TextInput
              size="md"
              ref={emailRef}
              placeholder="ornek@email.com"
              leftSection={<IconAt style={{ width: rem(18), height: rem(18) }} />}
              name="email"
              error={actionData?.errors?.email || form.errors.email}
              disabled={isSubmitting}
              {...form.getInputProps('email')}
              autoComplete="email"
              data-testid="email-input"
            />
          </Stack>

          <Button
            type="submit"
            mt="md"
            color="blue"
            radius="md"
            fullWidth
            loading={isSubmitting}
            data-testid="forgot-password-button"
          >
            {isSubmitting ? "Gönderiliyor..." : "Şifre Sıfırlama Bağlantısı Gönder"}
          </Button>

          <Group justify="center" mt="md">
            <Anchor component={Link} to="/login" size="sm" c="white">
              <Group gap="xs">
                <IconArrowLeft size={16} />
                <Text>Giriş Sayfasına Dön</Text>
              </Group>
            </Anchor>
          </Group>
        </Stack>
      </Form>
    </div>
  );
}
